/**
 * ThemeProvider
 *
 * Owns the light/dark/system theme preference and applies it to the document.
 *
 * Responsibilities:
 * - Persist the user's choice to localStorage under THEME_STORAGE_KEY
 * - Resolve "system" against prefers-color-scheme, and keep following the OS
 *   while "system" is selected
 * - Toggle the `dark` class on <html>, which is what Tailwind's
 *   darkMode: ["class"] and the .dark token block in index.css key off
 * - Keep <meta name="theme-color"> in sync so mobile browser chrome and the
 *   installed PWA's status bar match the active theme
 *
 * The initial class is applied by an inline script in index.html, before first
 * paint — see that file. This provider must agree with it, or the page will
 * flash. Both read the same storage key and the same media query.
 *
 * Types and the context object live in theme-context.ts so this module exports
 * only components, which is what Fast Refresh requires.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  DARK_QUERY,
  THEME_COLORS,
  THEME_STORAGE_KEY,
  ThemeContext,
  type ResolvedTheme,
  type Theme,
} from "./theme-context";

function isTheme(value: unknown): value is Theme {
  return value === "light" || value === "dark" || value === "system";
}

function prefersDark(): boolean {
  // matchMedia is absent in jsdom without a shim, and in older Safari.
  return typeof window !== "undefined" &&
    typeof window.matchMedia === "function"
    ? window.matchMedia(DARK_QUERY).matches
    : false;
}

function readStoredTheme(): Theme {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (isTheme(stored)) return stored;
  } catch {
    // Safari private mode throws on localStorage access. Fall through to
    // "system", which still renders correctly — only persistence is lost.
  }
  return "system";
}

function resolve(theme: Theme): ResolvedTheme {
  return theme === "system" ? (prefersDark() ? "dark" : "light") : theme;
}

function applyTheme(resolved: ResolvedTheme): void {
  const root = document.documentElement;
  root.classList.toggle("dark", resolved === "dark");
  // Explicit attribute so non-Tailwind consumers can read the active theme.
  root.dataset.theme = resolved;

  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", THEME_COLORS[resolved]);
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(readStoredTheme);
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() =>
    resolve(readStoredTheme())
  );

  // Apply on mount and whenever the preference changes.
  useEffect(() => {
    const next = resolve(theme);
    setResolvedTheme(next);
    applyTheme(next);
  }, [theme]);

  // While on "system", keep following the OS. Any explicit choice detaches.
  useEffect(() => {
    if (theme !== "system") return;
    if (typeof window.matchMedia !== "function") return;

    const query = window.matchMedia(DARK_QUERY);
    const onChange = (event: MediaQueryListEvent) => {
      const next: ResolvedTheme = event.matches ? "dark" : "light";
      setResolvedTheme(next);
      applyTheme(next);
    };

    // Safari < 14 only has the deprecated addListener.
    if (typeof query.addEventListener === "function") {
      query.addEventListener("change", onChange);
      return () => query.removeEventListener("change", onChange);
    }
    query.addListener(onChange);
    return () => query.removeListener(onChange);
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // Persistence is best-effort; the in-memory choice still applies.
    }
  }, []);

  const toggleTheme = useCallback(() => {
    // From "system", switch to the opposite of what is currently shown, so the
    // button always visibly does something.
    setTheme(resolve(theme) === "dark" ? "light" : "dark");
  }, [theme, setTheme]);

  const value = useMemo(
    () => ({ theme, resolvedTheme, setTheme, toggleTheme }),
    [theme, resolvedTheme, setTheme, toggleTheme]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}
